/** Records of Barangay Inhabitants: search and filters on top, the list below (FR-RES-01, FR-RES-03). */
import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { FileDown, Plus, Upload } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { downloadFile } from '../../lib/files';
import { Button, Card, EmptyState, FilterBar, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';
import { barangaySettings, sectorLabel, STATUS, type ResidentRow } from './types';

export function ResidentsPage(): JSX.Element {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const settings = useQuery(barangaySettings);
  const get = (k: string) => params.get(k) ?? '';
  const set = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); if (k !== 'page') p.delete('page'); setParams(p, { replace: true }); };
  useEffect(() => { const t = setTimeout(() => { if (q !== get('q')) set('q', q); }, 300); return () => clearTimeout(t); }); // eslint-disable-line react-hooks/exhaustive-deps
  const page = Number(get('page') || '1');
  const query = new URLSearchParams(params);
  query.set('pageSize', '50');
  const list = useQuery({ queryKey: ['residents', query.toString()], queryFn: () => api<{ data: ResidentRow[]; total: number; page: number; pageSize: number }>(`/residents?${query.toString()}`), placeholderData: keepPreviousData });
  const exportQuery = new URLSearchParams(params);
  exportQuery.delete('page');
  const sectors = ['SENIOR_CITIZEN', 'YOUTH', 'MINOR', ...(settings.data?.sectorTags ?? [])];
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / 50)) : 1;

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Residents"
        description="Records of Barangay Inhabitants. Inactive people (transferred out, deceased) keep their history and show under Status."
        actions={<>
          {can('residents.import') && <Link to="/registry/import"><Button variant="secondary"><Upload aria-hidden="true" className="h-4 w-4" /> Import</Button></Link>}
          {can('residents.export') && <Button variant="secondary" onClick={() => void downloadFile(`/residents/export.xlsx?${exportQuery.toString()}`).catch((e: Error) => window.alert(e.message))}><FileDown aria-hidden="true" className="h-4 w-4" /> Excel</Button>}
          {can('residents.manage') && <Link to="/residents/new"><Button><Plus aria-hidden="true" className="h-4 w-4" /> New resident</Button></Link>}
        </>}
      />
      <FilterBar>
        <input className={`${filterClass} w-64`} placeholder="Name, RES-number or PhilSys" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search residents" />
        <select className={filterClass} value={get('purok')} onChange={(e) => set('purok', e.target.value)} aria-label="Purok">
          <option value="">All puroks</option>
          {settings.data?.puroks.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className={filterClass} value={get('sector')} onChange={(e) => set('sector', e.target.value)} aria-label="Sector">
          <option value="">All sectors</option>
          {sectors.map((s) => <option key={s} value={s}>{sectorLabel(s)}</option>)}
        </select>
        <select className={filterClass} value={get('sex')} onChange={(e) => set('sex', e.target.value)} aria-label="Sex">
          <option value="">Any sex</option><option value="MALE">Male</option><option value="FEMALE">Female</option>
        </select>
        <input className={`${filterClass} w-24`} type="number" min={0} max={130} placeholder="Age min" value={get('ageMin')} onChange={(e) => set('ageMin', e.target.value)} aria-label="Minimum age" />
        <input className={`${filterClass} w-24`} type="number" min={0} max={130} placeholder="Age max" value={get('ageMax')} onChange={(e) => set('ageMax', e.target.value)} aria-label="Maximum age" />
        <select className={filterClass} value={get('status')} onChange={(e) => set('status', e.target.value)} aria-label="Status">
          <option value="">Active</option><option value="TRANSFERRED_OUT">Transferred out</option><option value="DECEASED">Deceased</option><option value="ALL">Everyone</option>
        </select>
      </FilterBar>
      <Card>
        {list.isLoading ? <Skeleton className="h-64 w-full" /> : !list.data?.data.length ? (
          <EmptyState title="No residents found" description={params.toString() ? 'Try fewer filters.' : 'Register residents one by one, or import your existing list.'} />
        ) : (
          <>
            <TableWrap>
              <thead><tr><Th>Name</Th><Th>Age / sex</Th><Th>Household</Th><Th>Sectors</Th><Th>Status</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {list.data.data.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td><Link to={`/residents/${r.id}`} className="font-medium text-slate-900 hover:underline dark:text-white">{r.displayName}</Link><div className="font-mono text-xs text-slate-500">{r.code}</div></Td>
                    <Td><span className="tabular-nums">{r.age}</span> · {r.sex === 'MALE' ? 'M' : 'F'}</Td>
                    <Td>{r.household ? <Link className="hover:underline" to={`/households/${r.household.id}`}><span className="font-mono text-xs">{r.household.code}</span> <span className="text-sm text-slate-500">{r.household.purok}</span></Link> : <span className="text-sm text-slate-400">—</span>}</Td>
                    <Td><div className="flex flex-wrap gap-1">{r.sectors.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{sectorLabel(s)}</span>)}</div></Td>
                    <Td><StatusChip tone={STATUS[r.status].tone}>{STATUS[r.status].label}</StatusChip></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
              <span>{list.data.total.toLocaleString()} {list.data.total === 1 ? 'person' : 'people'}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => set('page', String(page - 1))}>Previous</Button>
                <span>Page {page} of {pages}</span>
                <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => set('page', String(page + 1))}>Next</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
